import createTxpDataRow from './createTxpDataRow';
import convertDateTimeToString from './convertDateTimeToString';
import populateEventBusStopData from './populateEventBusStopData';
import ActivityType from '../../schema/innerSchema/ActivityType';
import BusStopData from '../../schema/viewSchema/BusStopData';
import CardItem from '../../schema/viewSchema/CardItem';
import DetailsIcons from '../../schema/configSchema/DetailsIcons';
import EntityType from '../../schema/innerSchema/EntityType';
import EventReservation from '../../schema/EventReservation';
import ActionNameConstants from '../../helpers/ActionNameConstants';
import Ticket from '../../schema/innerSchema/Ticket';
import TxpStrings from '../../schema/configSchema/TxpStrings';

// Extracts the event specific data needed for displaying the card
export default function createEventCard(
    eventEntity: EventReservation,
    isBusStop: boolean,
    activityId: string,
    showLocation: boolean,
    entityImage: string,
    txpStrings: TxpStrings,
    entityIcon?: string,
    calendarEventId?: string,
    detailsIcons?: DetailsIcons
): CardItem {
    let busStopData: BusStopData;
    let locationText: string;

    let locationInfo = eventEntity.location ? eventEntity.location : null;
    let ticketNumber = eventEntity.reservedTicket ? eventEntity.reservedTicket.ticketNumber : null;

    let summaryFirstLine = txpStrings.formatters.format(
        txpStrings.strings.reservationText,
        eventEntity.eventName
    );
    let summarySecondLine =
        eventEntity.reservationId &&
        txpStrings.formatters.format(
            txpStrings.strings.reservationCodeText,
            eventEntity.reservationId
        );

    //startTime stores string for first line in the description of event card.
    let startTime = convertDateTimeToString(eventEntity.startDate, false, txpStrings.formatters);
    let ariaStartTime = convertDateTimeToString(eventEntity.startDate, true, txpStrings.formatters);
    let personName = eventEntity.underName.name;

    //allAttendees stores string for second line in the description of event card.
    let allAttendees = personName;
    if (eventEntity.numSeats == 1) {
        allAttendees = txpStrings.formatters.format(
            txpStrings.strings.eventReservationWithOneGuestText,
            personName
        );
    } else if (eventEntity.numSeats > 1) {
        allAttendees = txpStrings.formatters.format(
            txpStrings.strings.eventReservationWithGuestsText,
            personName,
            eventEntity.numSeats
        );
    }

    if (locationInfo) {
        if (locationInfo.name) {
            locationText = locationInfo.name;
        } else if (locationInfo.address.streetAddress) {
            locationText = locationInfo.address.streetAddress;
        }
    }

    const actionButtonData = eventEntity.modifyReservationUrl
        ? {
              actionIconName: detailsIcons.edit,
              actionUrl: eventEntity.modifyReservationUrl,
              actionButtonText: txpStrings.strings.modifyReservationText,
              actionName: ActionNameConstants.ModifyReservation,
          }
        : null;

    let ticketInfo: string[] = [];

    if (ticketNumber) {
        ticketInfo.push(ticketNumber);
    }

    let seatDetails = getSeatDetailsText(eventEntity.reservedTicket, txpStrings);
    seatDetails && ticketInfo.push(seatDetails);

    let ticketData: string;
    if (ticketInfo.length == 2) {
        // TODO: Rename cityDateFormatText to a generic label 'TwoStringPlaceholderWithComma'
        ticketData = txpStrings.formatters.format(
            txpStrings.strings.cityDateFormatText,
            ...ticketInfo
        );
    } else {
        ticketData = ticketInfo[0] || null;
    }

    let detailsData = {
        firstLine: createTxpDataRow(
            startTime,
            txpStrings.strings.eventTimePrefixText,
            detailsIcons.clock
        ),
        secondLine: createTxpDataRow(
            allAttendees,
            txpStrings.strings.reservationForText,
            detailsIcons.contact
        ),
        thirdLine: createTxpDataRow(
            ticketData,
            txpStrings.strings.eventTicketLabel,
            detailsIcons.ticket
        ),
        fourthLine: showLocation
            ? createTxpDataRow(
                  locationText,
                  null,
                  detailsIcons.location,
                  false /* is required */,
                  null /* placeholder */,
                  true /* includeToolTip*/
              )
            : null,
    };

    if (isBusStop) {
        busStopData = populateEventBusStopData(
            eventEntity,
            startTime,
            ariaStartTime,
            txpStrings,
            detailsIcons.event
        );
    }

    return {
        id: activityId,
        summaryData: {
            firstLine: { text: summaryFirstLine },
            secondLine: { text: summarySecondLine },
        },
        entityIcon: entityIcon,
        entityImage: entityImage,
        activityType: ActivityType.EventActivityType,
        entityType: EntityType.EventReservation,
        detailsData: detailsData,
        actionButtonData: actionButtonData,
        calendarEventId: calendarEventId,
        ariaText: txpStrings.formatters.format(
            txpStrings.strings.eventAriaFormatText,
            ariaStartTime,
            detailsData.secondLine && detailsData.secondLine.text,
            detailsData.thirdLine && detailsData.thirdLine.text,
            locationText
        ),
        busStopData: busStopData,
        brandsSummaryFirstLine: eventEntity.eventName,
        brandsSummarySecondLine: txpStrings.formatters.formatWeekDayDateTime(eventEntity.startDate),
    } as CardItem;
}

function getSeatDetailsText(reservedSeat: Ticket, txpStrings: TxpStrings): string {
    let seatDetailsText: string = null;
    let seatDetailsLabel: string;
    const {
        eventSectionRowSeatPlaceholder,
        eventSeatRowPlaceholder,
        eventSeatPlaceholder,
    } = txpStrings.strings;
    if (reservedSeat && reservedSeat.seat && reservedSeat.seat.seatNumber) {
        let { row, seatNumber, section } = reservedSeat.seat;
        if (row) {
            if (section) {
                seatDetailsLabel = eventSectionRowSeatPlaceholder;
                seatDetailsText = txpStrings.formatters.format(
                    seatDetailsLabel,
                    section,
                    row,
                    seatNumber
                );
            } else {
                seatDetailsLabel = eventSeatRowPlaceholder;
                seatDetailsText = txpStrings.formatters.format(seatDetailsLabel, row, seatNumber);
            }
        } else {
            seatDetailsLabel = eventSeatPlaceholder;
            seatDetailsText = txpStrings.formatters.format(seatDetailsLabel, seatNumber);
        }
    }
    return seatDetailsText;
}
